/**
 * Studio Admin → Runs → Build KPIs: how builds go across every organization, per day or week,
 * by agent engine and model level. Read from the runs and events Studio already records.
 * Report-only: nothing here gates a run.
 */
import { RotateCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  type AdminAgentEngine,
  type AdminRunFailureClass,
  type AdminRunKpiLevel,
  type AdminRunKpiValues,
  type AdminRunKpis,
  adminClient,
} from '@/api/adminClient';
import { SeriesKey, trendDayLabel } from '@/components/dashboard/DashboardTrends';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { LoadError, Skeleton } from '@/components/ui/load-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { chartAxisTick, chartBarCursor, chartSeriesColor, chartTooltipProps } from '@/lib/chartTheme';
import { formatCount, formatUsd, rangeForDays } from '@/lib/format';
import { cn } from '@/lib/utils';

export const FAILURE_CLASS_LABELS: Record<AdminRunFailureClass, string> = {
  stopped: 'Stopped or replaced by a newer request',
  preview_build: 'Preview build or checks',
  delivery: 'Saving the version',
  workspace: 'Workspace (MicroVM)',
  timeout: 'Timed out',
  model: 'Model or gateway',
  orchestration: 'Orchestration (Temporal)',
  no_change: 'Agent made no change',
  unrecorded: 'No error recorded',
  other: 'Other',
};

const ENGINE_LABELS: Record<AdminAgentEngine, string> = { omp: 'OMP', opencode: 'OpenCode' };

const LEVEL_LABELS: Record<AdminRunKpiLevel, string> = {
  auto: 'Auto',
  quick: 'Quick',
  light: 'Light',
  standard: 'Standard',
  advanced: 'Advanced',
  expert: 'Expert',
};

const WINDOWS = [7, 30, 90] as const;
const ALL = 'all';

/** "62%", or "—" with nothing to divide by. */
export function formatRate(rate: number | null | undefined): string {
  return rate === null || rate === undefined || !Number.isFinite(rate) ? '—' : `${Math.round(rate * 100)}%`;
}

/** "45s", "3m 20s", "1h 5m", or "—". */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  if (total < 3600) return total % 60 ? `${Math.floor(total / 60)}m ${total % 60}s` : `${total / 60}m`;
  const minutes = Math.round((total % 3600) / 60);
  return minutes ? `${Math.floor(total / 3600)}h ${minutes}m` : `${Math.floor(total / 3600)}h`;
}

/** The change in a rate against the previous window, in points: "+4 pts", "−10 pts". */
export function pointsDelta(current: number | null, previous: number | null): string | undefined {
  if (current === null || previous === null) return undefined;
  const points = Math.round((current - previous) * 100);
  if (points === 0) return '0 pts';
  return `${points > 0 ? '+' : '−'}${Math.abs(points)} pts`;
}

function Tile({ label, value, detail, change }: { label: string; value: string; detail?: string; change?: string }) {
  return (
    <div className="rounded-lg border border-border bg-background/40 px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</p>
      {detail ? <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p> : null}
      {change ? <p className="mt-0.5 text-xs text-muted-foreground">{change} vs previous window</p> : null}
    </div>
  );
}

function Tiles({ data }: { data: AdminRunKpis }) {
  const t = data.totals;
  const p = data.previous;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <Tile
        label="Builds that succeeded"
        value={formatRate(t.successRate)}
        detail={`${formatCount(t.succeeded)} of ${formatCount(t.builds)} ended${t.inFlight ? `, ${formatCount(t.inFlight)} still running` : ''}`}
        change={pointsDelta(t.successRate, p.successRate)}
      />
      <Tile
        label="Builds that failed"
        value={formatRate(t.failureRate)}
        detail={`${formatCount(t.failed)} failed${t.blocked ? `, ${formatCount(t.blocked)} blocked` : ''}`}
        change={pointsDelta(t.failureRate, p.failureRate)}
      />
      <Tile
        label="Prompt to first preview"
        value={`${formatSeconds(t.previewP50Seconds)} median`}
        detail={`${formatSeconds(t.previewP95Seconds)} at p95, over ${formatCount(t.previewSamples)} builds`}
      />
      <Tile
        label="Builds Studio repaired in the background"
        value={formatRate(t.repairRate)}
        detail={`${formatCount(t.structuralRepaired)} structural, ${formatCount(t.qualityRepaired)} quality`}
        change={pointsDelta(t.repairRate, p.repairRate)}
      />
      <Tile
        label="Checked builds with blocking findings"
        value={formatRate(t.blockingRate)}
        detail={`${formatCount(t.checked)} of ${formatCount(t.builds)} builds checked · ${formatCount(t.fixFirst)} handed to the next message (Fix First)`}
        change={pointsDelta(t.blockingRate, p.blockingRate)}
      />
      <Tile
        label="Cost per successful build"
        value={t.costPerSuccessfulBuildUsd === null ? '—' : formatUsd(t.costPerSuccessfulBuildUsd)}
        detail={`${formatUsd(t.costUsd)} on builds and their repairs`}
        change={
          t.costPerSuccessfulBuildUsd !== null && p.costPerSuccessfulBuildUsd
            ? `${t.costPerSuccessfulBuildUsd >= p.costPerSuccessfulBuildUsd ? '+' : '−'}${formatUsd(Math.abs(t.costPerSuccessfulBuildUsd - p.costPerSuccessfulBuildUsd))}`
            : undefined
        }
      />
    </div>
  );
}

function bucketLabel(data: AdminRunKpis, start: string): string {
  return data.bucket === 'week' ? `Week of ${trendDayLabel(start)}` : trendDayLabel(start);
}

function TrendCharts({ data }: { data: AdminRunKpis }) {
  const rows = data.series.map((row) => ({
    label: bucketLabel(data, row.start),
    succeeded: row.succeeded,
    failed: row.failed + row.blocked,
    successRate: row.successRate === null ? null : Math.round(row.successRate * 100),
    p50: row.previewP50Seconds === null ? null : Math.round(row.previewP50Seconds / 60),
    p95: row.previewP95Seconds === null ? null : Math.round(row.previewP95Seconds / 60),
  }));
  const names: Record<string, string> = {
    succeeded: 'Succeeded',
    failed: 'Failed or blocked',
    successRate: 'Success rate (%)',
    p50: 'Median (min)',
    p95: 'p95 (min)',
  };
  const tooltip = (
    <Tooltip {...chartTooltipProps} cursor={chartBarCursor} formatter={(value, name) => [String(value), names[String(name)] ?? name]} />
  );
  const axis = <XAxis dataKey="label" tick={chartAxisTick} interval="preserveStartEnd" minTickGap={60} tickLine={false} axisLine={false} height={18} />;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <p className="text-xs font-medium text-foreground">Builds and success rate</p>
        <div className="mt-2 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              {axis}
              <YAxis yAxisId="count" hide />
              <YAxis yAxisId="rate" hide domain={[0, 100]} />
              {tooltip}
              <Bar yAxisId="count" dataKey="succeeded" stackId="builds" fill={chartSeriesColor(1)} />
              <Bar yAxisId="count" dataKey="failed" stackId="builds" fill={chartSeriesColor(3)} radius={[2, 2, 0, 0]} />
              <Line yAxisId="rate" type="monotone" dataKey="successRate" stroke={chartSeriesColor(0)} strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <SeriesKey items={[{ label: 'Succeeded', colorIndex: 1 }, { label: 'Failed or blocked', colorIndex: 3 }, { label: 'Success rate', colorIndex: 0 }]} />
      </div>
      <div>
        <p className="text-xs font-medium text-foreground">Prompt to first preview (minutes)</p>
        <div className="mt-2 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              {axis}
              <YAxis hide />
              {tooltip}
              <Line type="monotone" dataKey="p50" stroke={chartSeriesColor(0)} strokeWidth={1.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="p95" stroke={chartSeriesColor(2)} strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <SeriesKey items={[{ label: 'Median', colorIndex: 0 }, { label: 'p95', colorIndex: 2 }]} />
      </div>
    </div>
  );
}

function FailureClasses({ values }: { values: AdminRunKpiValues }) {
  const rows = (Object.entries(values.failureClasses) as Array<[AdminRunFailureClass, number]>)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No build failed in this window.</p>;
  return (
    <DataTable>
      <DataTableHead columns={[{ label: 'What went wrong' }, { label: 'Builds', className: 'text-right' }, { label: 'Of all builds', className: 'text-right' }]} />
      <tbody>
        {rows.map(([failureClass, count]) => (
          <DataTableRow key={failureClass}>
            <DataTableCell>{FAILURE_CLASS_LABELS[failureClass]}</DataTableCell>
            <DataTableCell className="text-right tabular-nums">{formatCount(count)}</DataTableCell>
            <DataTableCell className="text-right tabular-nums">{formatRate(values.builds ? count / values.builds : null)}</DataTableCell>
          </DataTableRow>
        ))}
      </tbody>
    </DataTable>
  );
}

function Breakdown({ title, rows }: { title: string; rows: Array<{ key: string; label: string; values: AdminRunKpiValues }> }) {
  const shown = rows.filter((row) => row.values.builds + row.values.inFlight > 0);
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-foreground">{title}</p>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">No builds.</p>
      ) : (
        <DataTable>
          <DataTableHead
            columns={[
              { label: title },
              { label: 'Builds', className: 'text-right' },
              { label: 'Succeeded', className: 'text-right' },
              { label: 'First preview (median)', className: 'text-right' },
              { label: 'Repaired', className: 'text-right' },
              { label: 'Blocking findings', className: 'text-right' },
              { label: 'Cost per success', className: 'text-right' },
            ]}
          />
          <tbody>
            {shown.map(({ key, label, values }) => (
              <DataTableRow key={key}>
                <DataTableCell className="font-medium">{label}</DataTableCell>
                <DataTableCell className="text-right tabular-nums">{formatCount(values.builds)}</DataTableCell>
                <DataTableCell className="text-right tabular-nums">{formatRate(values.successRate)}</DataTableCell>
                <DataTableCell className="text-right tabular-nums">{formatSeconds(values.previewP50Seconds)}</DataTableCell>
                <DataTableCell className="text-right tabular-nums">{formatRate(values.repairRate)}</DataTableCell>
                <DataTableCell className="text-right tabular-nums">{formatRate(values.blockingRate)}</DataTableCell>
                <DataTableCell className="text-right tabular-nums">
                  {values.costPerSuccessfulBuildUsd === null ? '—' : formatUsd(values.costPerSuccessfulBuildUsd)}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}

/** The numbers for one loaded window. */
export function RunKpisView({ data }: { data: AdminRunKpis }) {
  if (data.totals.builds + data.totals.inFlight === 0) {
    return <p className="text-sm text-muted-foreground">No builds in this window{data.filters.engine || data.filters.level ? ' for these filters' : ''}.</p>;
  }
  return (
    <div className="flex flex-col gap-6">
      <Tiles data={data} />
      <TrendCharts data={data} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-medium text-foreground">Failures by kind</p>
          <FailureClasses values={data.totals} />
        </div>
        <div>
          <p className="mb-2 text-xs font-medium text-foreground">Most common failure messages</p>
          {data.topErrors.length === 0 ? (
            <p className="text-sm text-muted-foreground">No failure messages recorded.</p>
          ) : (
            <DataTable>
              <DataTableHead columns={[{ label: 'Message' }, { label: 'Kind' }, { label: 'Builds', className: 'text-right' }]} />
              <tbody>
                {data.topErrors.map((row) => (
                  <DataTableRow key={row.error}>
                    <DataTableCell className="max-w-[22rem] font-mono text-xs">
                      <span className="line-clamp-2">{row.error}</span>
                    </DataTableCell>
                    <DataTableCell className="text-muted-foreground">{FAILURE_CLASS_LABELS[row.failureClass]}</DataTableCell>
                    <DataTableCell className="text-right tabular-nums">{formatCount(row.count)}</DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          )}
        </div>
      </div>
      <Breakdown title="Agent" rows={data.byEngine.map((row) => ({ key: row.engine, label: ENGINE_LABELS[row.engine], values: row }))} />
      <Breakdown title="Model level" rows={data.byLevel.map((row) => ({ key: row.level, label: LEVEL_LABELS[row.level], values: row }))} />
      <p className="text-xs leading-5 text-muted-foreground">
        A build is a person's turn that runs the agent: not a plan, a Chat answer, a restore or a background repair. Rates count builds that ended in the window.
        Time to first preview runs from the build's start to its first preview. Repairs are background repairs Studio started for the build. Blocking findings are
        from the build's own checks, before any repair. Cost adds each build's repairs and leaves plan turns and Chat out. Days and weeks are in {data.timeZone}.
        {data.truncated ? ' This window has more runs than one read returns, so the oldest part is missing; pick a shorter window.' : ''}
      </p>
    </div>
  );
}

function FilterSelect<T extends string>({ label, value, onChange, options }: { label: string; value: T; onChange: (value: T) => void; options: Array<{ value: T; label: string }> }) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as T)}>
      <SelectTrigger size="sm" className="w-36" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function RunKpis() {
  const [days, setDays] = useState<string>('30');
  const [bucket, setBucket] = useState<'day' | 'week'>('day');
  const [engine, setEngine] = useState<AdminAgentEngine | typeof ALL>(ALL);
  const [level, setLevel] = useState<AdminRunKpiLevel | typeof ALL>(ALL);
  const [data, setData] = useState<AdminRunKpis | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(undefined);
    try {
      setData(
        await adminClient.getRunKpis({
          ...rangeForDays(Number(days)),
          bucket,
          ...(engine === ALL ? {} : { engine }),
          ...(level === ALL ? {} : { level }),
        }),
      );
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load build KPIs');
    } finally {
      setLoading(false);
    }
  }, [days, bucket, engine, level]);

  useEffect(() => {
    void load();
  }, [load]);

  const actions = (
    <>
      <FilterSelect label="Window" value={days} onChange={setDays} options={WINDOWS.map((d) => ({ value: String(d), label: `Last ${d} days` }))} />
      <FilterSelect label="Group by" value={bucket} onChange={setBucket} options={[{ value: 'day', label: 'By day' }, { value: 'week', label: 'By week' }]} />
      <FilterSelect
        label="Agent"
        value={engine}
        onChange={setEngine}
        options={[{ value: ALL, label: 'Every agent' }, ...(Object.keys(ENGINE_LABELS) as AdminAgentEngine[]).map((e) => ({ value: e, label: ENGINE_LABELS[e] }))]}
      />
      <FilterSelect
        label="Model level"
        value={level}
        onChange={setLevel}
        options={[{ value: ALL, label: 'Every level' }, ...(Object.keys(LEVEL_LABELS) as AdminRunKpiLevel[]).map((l) => ({ value: l, label: LEVEL_LABELS[l] }))]}
      />
      <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading} aria-label="Refresh build KPIs">
        <RotateCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} aria-hidden="true" />
      </Button>
    </>
  );

  return (
    <AdminSection title="Build KPIs" description="How builds go across every organization. Report-only." actions={actions}>
      {!data && loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Loading build KPIs">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : !data ? (
        <LoadError message={loadError} onRetry={() => void load()} />
      ) : (
        <div className={cn(loading && 'opacity-60')}>
          {loadError ? <p className="mb-3 text-sm text-destructive-foreground">Could not refresh: {loadError}</p> : null}
          <RunKpisView data={data} />
        </div>
      )}
    </AdminSection>
  );
}
